import React, { useState, useEffect } from 'react';
import { Menu, X, Instagram } from 'lucide-react';

const INSTAGRAM_URL = 'https://www.instagram.com/gal_war_cc/?utm_source=ig_web_button_share_sheet';

/* All sections live on ONE continuous-scroll page; nav smooth-scrolls to them. */
const NAV_ITEMS = [
  { name: 'HOME', id: 'home' },
  { name: 'FACTIONS', id: 'races' },
  { name: 'MAPS', id: 'map' },
  { name: 'ARSENAL', id: 'weapons' },
  { name: 'NEWS', id: 'features' },
  { name: 'ABOUT', id: 'about' },
];

// Full section map for the dropdown menu
const MENU_ITEMS = [
  { name: 'HOME', id: 'home' },
  { name: 'CONQUEST', id: 'gameplay' },
  { name: 'FACTIONS', id: 'races' },
  { name: 'MAPS', id: 'map' },
  { name: 'ARSENAL', id: 'weapons' },
  { name: 'TERRAFORM', id: 'terraform' },
  { name: 'NEWS', id: 'features' },
  { name: 'TEAM', id: 'team' },
  { name: 'EXPANSION', id: 'future' },
  { name: 'ABOUT', id: 'about' },
];

const NAV_OFFSET = 72;

const scrollToId = (id) => {
  if (id === 'home') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
  window.scrollTo({ top: y, behavior: 'smooth' });
};

const WingEmblem = () => (
  <img
    src="/galwar-logo.png"
    alt="GAL WAR CC"
    className="w-11 h-11 flex-shrink-0 object-contain"
    style={{ border: 'none', background: 'transparent', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.28))' }}
  />
);

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState('home');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll-spy: highlight the section currently near the viewport center
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActiveId(e.target.id); }),
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 }
    );
    MENU_ITEMS.forEach((m) => { const el = document.getElementById(m.id); if (el) io.observe(el); });
    return () => io.disconnect();
  }, []);

  const go = (id) => { scrollToId(id); setMenuOpen(false); };

  const logoColor = '#F5F7F8';
  const iconColor = '#B8C2D2';
  const dividerColor = 'rgba(184,194,210,0.25)';

  return (
    <nav
      data-testid="main-nav"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled || menuOpen ? 'rgba(9, 13, 18, 0.92)' : 'transparent',
        backdropFilter: scrolled || menuOpen ? 'blur(12px)' : 'none',
        WebkitBackdropFilter: scrolled || menuOpen ? 'blur(12px)' : 'none',
      }}
    >
      <div className="max-w-[1480px] mx-auto px-6 py-3 flex items-center justify-between gap-6">
        {/* LEFT — Logo (borderless) */}
        <button
          onClick={() => go('home')}
          data-testid="nav-logo"
          className="flex items-center gap-3 flex-shrink-0 group"
          style={{ background: 'transparent', border: 'none', padding: 0 }}
        >
          <WingEmblem />
          <span
            className="text-base sm:text-lg font-black tracking-[0.18em] hidden sm:inline"
            style={{
              fontFamily: '"Rajdhani", sans-serif',
              fontWeight: 700,
              color: logoColor,
              textShadow: '0 0 12px rgba(245,247,248,0.25), 0 2px 6px rgba(0,0,0,0.55)',
              transition: 'color 0.3s ease',
            }}
          >
            GAL WAR CC
          </span>
        </button>

        {/* CENTER — Nav links */}
        <div className="hidden md:flex items-center gap-6 lg:gap-9 flex-1 justify-center">
          {NAV_ITEMS.map((item) => {
            const isActive = activeId === item.id;
            const color = isActive ? '#FFFFFF' : '#A8B0BD';
            return (
              <button
                key={item.id}
                onClick={() => go(item.id)}
                data-testid={`nav-${item.name.toLowerCase()}`}
                className="nav-link relative text-xs font-bold tracking-[0.24em] uppercase"
                style={{
                  fontFamily: '"Rajdhani", sans-serif',
                  fontWeight: 700,
                  color,
                  padding: '7px 15px',
                  borderRadius: '9px',
                  background: isActive ? 'rgba(255,134,24,0.16)' : 'transparent',
                  border: isActive ? '1px solid rgba(255,134,24,0.65)' : '1px solid transparent',
                  boxShadow: isActive ? '0 0 18px rgba(255,134,24,0.28), inset 0 0 12px rgba(255,134,24,0.08)' : 'none',
                  textShadow: isActive ? '0 0 12px rgba(255,134,24,0.6), 0 2px 4px rgba(0,0,0,0.55)' : '0 1px 2px rgba(0,0,0,0.5)',
                  transition: 'color 0.25s ease, background-color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
                }}
              >
                {item.name}
              </button>
            );
          })}
        </div>

        {/* RIGHT — Social icons + Menu */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <a href="#" data-testid="social-discord" className="social-ico hidden sm:flex w-8 h-8 items-center justify-center" style={{ color: iconColor, transition: 'color 0.3s ease' }} aria-label="Discord">
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
              <path d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4a.09.09 0 0 0-.07.03c-.18.33-.39.76-.53 1.09a16.09 16.09 0 0 0-4.8 0c-.14-.34-.35-.76-.54-1.09a.1.1 0 0 0-.07-.03c-1.5.26-2.93.71-4.27 1.33a.06.06 0 0 0-.03.03C2.3 9.36 1.55 13.27 1.92 17.14c0 .02.01.04.03.05 1.79 1.31 3.52 2.11 5.23 2.64a.1.1 0 0 0 .08-.03c.4-.55.76-1.13 1.07-1.74.02-.04 0-.08-.04-.09-.57-.22-1.11-.48-1.64-.78a.07.07 0 0 1 0-.12c.11-.08.22-.17.33-.25a.07.07 0 0 1 .07-.01c3.44 1.57 7.15 1.57 10.55 0a.07.07 0 0 1 .08.01c.11.09.22.17.33.26a.07.07 0 0 1 0 .12c-.53.31-1.07.56-1.64.78-.04.01-.06.06-.04.09.31.61.67 1.19 1.07 1.74a.1.1 0 0 0 .08.03c1.72-.53 3.45-1.33 5.24-2.64a.08.08 0 0 0 .03-.05c.44-4.47-.74-8.34-3.13-11.77a.05.05 0 0 0-.03-.03zM8.52 14.78c-1.03 0-1.88-.94-1.88-2.1s.84-2.1 1.88-2.1c1.05 0 1.89.95 1.87 2.1 0 1.16-.84 2.1-1.87 2.1zm6.97 0c-1.03 0-1.88-.94-1.88-2.1s.84-2.1 1.88-2.1c1.05 0 1.89.95 1.87 2.1 0 1.16-.83 2.1-1.87 2.1z" />
            </svg>
          </a>
          <a href="#" data-testid="social-twitter" className="social-ico hidden sm:flex w-8 h-8 items-center justify-center" style={{ color: iconColor, transition: 'color 0.3s ease' }} aria-label="Twitter">
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
          <a href="#" data-testid="social-youtube" className="social-ico hidden sm:flex w-8 h-8 items-center justify-center" style={{ color: iconColor, transition: 'color 0.3s ease' }} aria-label="YouTube">
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
          </a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="social-ico hidden sm:flex w-8 h-8 items-center justify-center" style={{ color: iconColor, transition: 'color 0.3s ease' }} aria-label="Instagram">
            <Instagram className="w-5 h-5" />
          </a>
          <span className="hidden sm:block h-6 w-px" style={{ background: dividerColor }} />
          <button
            data-testid="nav-menu"
            onClick={() => setMenuOpen((o) => !o)}
            aria-expanded={menuOpen}
            className="social-ico w-9 h-9 flex items-center justify-center"
            style={{ border: 'none', background: 'transparent', color: menuOpen ? '#FF8618' : iconColor, transition: 'color 0.3s ease' }}
            aria-label="Menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* FULL-SECTION DROPDOWN MENU */}
      {menuOpen && (
        <div
          data-testid="nav-menu-panel"
          className="absolute left-0 right-0 top-full"
          style={{ background: 'rgba(9,13,18,0.97)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderTop: '1px solid rgba(255,134,24,0.25)', borderBottom: '1px solid rgba(255,134,24,0.25)' }}
        >
          <div className="max-w-[1480px] mx-auto px-6 py-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {MENU_ITEMS.map((item) => {
              const isActive = activeId === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => go(item.id)}
                  data-testid={`menu-${item.name.toLowerCase()}`}
                  className="group text-left px-4 py-3 rounded-lg"
                  style={{
                    background: isActive ? 'rgba(255,134,24,0.12)' : 'rgba(20,27,34,0.6)',
                    border: isActive ? '1px solid rgba(255,134,24,0.6)' : '1px solid rgba(255,255,255,0.1)',
                    transition: 'border-color 0.25s ease, background-color 0.25s ease',
                  }}
                >
                  <span
                    className="text-xs font-bold tracking-[0.24em] uppercase"
                    style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, color: isActive ? '#FF8618' : 'var(--text-muted)' }}
                  >
                    {item.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
